import React, { useState } from 'react';
import { getEmojiAssetUrl } from '../lib/emojiUnified';
import { cn } from '../lib/utils';

// Regex to detect emojis in text (standard Unicode Emoji pattern)
const EMOJI_REGEX =
  /(\p{Emoji_Presentation}|\p{Extended_Pictographic})(\u{FE0F})?(\u{20E3})?(\u{200D}(\p{Emoji_Presentation}|\p{Extended_Pictographic})(\u{FE0F})?(\u{20E3})?(\u{200D}(\p{Emoji_Presentation}|\p{Extended_Pictographic})(\u{FE0F})?(\u{20E3})?)*)*/gu;

const URL_REGEX = /https?:\/\/[^\s"'<>()]+/gi;

export function ReactionEmoji({ emoji, size = 18, className }) {
  const [hasError, setHasError] = useState(false);
  if (!emoji) return null;

  const url = getEmojiAssetUrl(emoji);

  if (hasError || !url) {
    return (
      <span className={cn('inline-flex shrink-0 items-center justify-center text-sm', className)}>
        {emoji}
      </span>
    );
  }

  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center leading-none select-none', className)}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <img
        src={url}
        alt={emoji}
        width={size}
        height={size}
        className="h-full w-full object-contain pointer-events-none"
        loading="lazy"
        onError={() => setHasError(true)}
      />
    </span>
  );
}

function splitLinks(text) {
  const parts = [];
  let last = 0;
  const regex = new RegExp(URL_REGEX.source, 'gi');
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) {
      parts.push({ type: 'plain', value: text.slice(last, match.index) });
    }
    parts.push({ type: 'link', value: match[0] });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    parts.push({ type: 'plain', value: text.slice(last) });
  }
  return parts;
}

function splitText(text) {
  if (!text) return [];
  const segments = [];
  let lastIndex = 0;
  for (const match of text.matchAll(EMOJI_REGEX)) {
    if (!match[0]) continue;
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'emoji', value: match[0] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return segments;
}

export function TwemojiText({ text, size = 18, className, style, disableLinks = false }) {
  const segments = splitText(text);

  return (
    <span
      className={cn('inline', className)}
      style={{ ...style, wordBreak: 'break-word', overflowWrap: 'break-word' }}
    >
      {segments.map((seg, i) => {
        if (seg.type === 'emoji') {
          return (
            <ReactionEmoji
              key={i}
              emoji={seg.value}
              size={size}
              className="inline-block align-middle mx-0.5"
            />
          );
        }
        if (disableLinks) {
          return <span key={i}>{seg.value}</span>;
        }
        const parts = splitLinks(seg.value);
        return (
          <span key={i}>
            {parts.map((part, j) =>
              part.type === 'link' ? (
                <a
                  key={j}
                  href={part.value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-500 underline underline-offset-2 hover:opacity-80 break-all max-w-full inline-block font-semibold"
                  onClick={(e) => e.stopPropagation()}
                >
                  {part.value}
                </a>
              ) : (
                <span key={j}>{part.value}</span>
              )
            )}
          </span>
        );
      })}
    </span>
  );
}

export default TwemojiText;
